// src/frontend_react/src/pages/UserDashboard/RunLinkageTab.js
import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// UI Components
import {
  FiZap,
  FiXCircle,
  FiCheckCircle,
  FiList,
  FiAlertTriangle,
  FiClock,
} from 'react-icons/fi';
import Button from '../../components/ui/Button/Button';
import Card from '../../components/ui/Card/Card';
import Icon from '../../components/ui/Icon/Icon';
import Modal from '../../components/ui/Modal/Modal';
import Spinner from '../../components/ui/Spinner/Spinner';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Typography from '../../components/ui/Typography/Typography';
import Input from '../../components/ui/Input/Input';
import Alert from '../../components/ui/Alert/Alert';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';

import { notify } from '../../services/notificationService';
import * as api from '../../services/api';
import styles from './RunLinkageTab.module.css'; // Make sure this CSS file exists

const MINIMUM_SOURCES_FOR_LINKAGE = 2;

const RunLinkageTab = () => {
  const { batchId } = useParams();
  const queryClient = useQueryClient();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  const {
    data: job,
    isLoading: isLoadingJobDetails,
    isError,
    error: jobError,
    isRefetching,
  } = useQuery({
    queryKey: ['jobDetails', batchId],
    queryFn: () => api.getBatchDetails(batchId),
    enabled: !!batchId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'processing' || status === 'finalized' ? 5000 : false;
    },
  });

  const finalizeJobMutation = useMutation({
    mutationFn: () => api.runLinkageForBatch(job.project_id, job.id),
    onSuccess: (data) => {
      notify.success(
        data.message ||
          'Job finalized successfully. Processing will begin shortly.',
      );
      queryClient.setQueryData(['jobDetails', batchId], (oldData) => ({
        ...oldData,
        status: 'finalized', // Optimistically update to 'finalized'
      }));
      queryClient.invalidateQueries({ queryKey: ['jobDetails', batchId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', job.project_id] });
    },
    onError: (error) => {
      notify.error(
        error.message || 'An error occurred while finalizing the job.',
      );
    },
    onSettled: () => {
      setShowConfirmModal(false);
      setConfirmationText('');
    },
  });

  const handleConfirmAndFinalize = () => {
    finalizeJobMutation.mutate();
  };

  if (isLoadingJobDetails) return <Spinner />;
  if (isError || !job) {
    return (
      <Alert variant="error">
        Could not load job details: {jobError?.message || 'Unknown error'}
      </Alert>
    );
  }

  // **IMPROVED "In Progress" View**
  if (
    job.status === 'processing' ||
    job.status === 'finalized' ||
    finalizeJobMutation.isPending
  ) {
    return (
      <Card>
        <Stack p="lg" alignItems="center" spacing="md">
          <Spinner size="lg" />
          <Typography variant="h4">Reconciliation in Progress</Typography>
          <Typography
            color="secondary"
            textAlign="center"
            style={{ maxWidth: '500px' }}
          >
            The server is processing the data. This page will update
            automatically when it's complete. You can safely navigate away and
            come back later.
          </Typography>

          <Box mt="md" width="100%" maxWidth="400px">
            <Stack spacing="sm">
              <HStack
                spacing="sm"
                alignItems="center"
                style={{
                  opacity:
                    job.status === 'finalized' || job.status === 'processing'
                      ? 1
                      : 0.4,
                }}
              >
                <Icon as={FiCheckCircle} color="success" />
                <Typography>Job Finalized</Typography>
              </HStack>
              <HStack
                spacing="sm"
                alignItems="center"
                style={{ opacity: job.status === 'processing' ? 1 : 0.4 }}
              >
                <Icon
                  as={job.status === 'processing' ? FiZap : FiClock}
                  color={job.status === 'processing' ? 'primary' : 'secondary'}
                />
                <Typography>Task Dispatched to Worker</Typography>
              </HStack>
              <HStack spacing="sm" alignItems="center" style={{ opacity: 0.4 }}>
                <Icon as={FiClock} color="secondary" />
                <Typography>Completing Reconciliation...</Typography>
              </HStack>
            </Stack>
          </Box>

          {isRefetching && (
            <Typography variant="caption" color="secondary" mt="sm">
              Checking for updates...
            </Typography>
          )}
        </Stack>
      </Card>
    );
  }

  const numPrepared = parseInt(job.progress?.submitted, 10) || 0;
  const hasExistingResults =
    job.files && job.files.some((f) => f.file_type === 'linkage_results');

  const canFinalize =
    job.status === 'in_progress' && numPrepared >= MINIMUM_SOURCES_FOR_LINKAGE;
  const canRerun =
    (job.status === 'completed' || job.status === 'failed') &&
    numPrepared >= MINIMUM_SOURCES_FOR_LINKAGE;

  const actionText =
    job.status === 'completed' || job.status === 'failed'
      ? 'Re-run Linkage'
      : 'Finalize and Run Reconciliation';
  const modalTitle =
    job.status === 'completed' || job.status === 'failed'
      ? 'Confirm Linkage Re-run'
      : 'Confirm Reconciliation Run';
  const modalWarning =
    job.status === 'completed' || job.status === 'failed'
      ? 'This will generate a new, versioned set of results based on the currently submitted data. Previous results will be preserved.'
      : 'This action will lock the job from further data contributions and start the reconciliation process.';

  return (
    <Stack spacing="lg">
      {(job.status === 'completed' || job.status === 'failed') && (
        <Alert
          variant={job.status === 'completed' ? 'success' : 'error'}
          title={`Job Previously ${job.status}`}
        >
          {job.status === 'completed'
            ? 'This job has already run successfully. You can view the output in the "Results" tab or re-run it below.'
            : `This job previously failed with reason: "${
                job.failure_reason || 'Unknown'
              }". You may attempt to re-run it.`}
        </Alert>
      )}

      <Card title="Pre-Flight Checklist" titleIcon={<Icon as={FiList} />}>
        <Stack as="ul" p="md" spacing="sm" style={{ listStyle: 'none' }}>
          <li key="check-sources">
            <HStack spacing="sm" alignItems="center">
              <Icon
                as={
                  numPrepared >= MINIMUM_SOURCES_FOR_LINKAGE
                    ? FiCheckCircle
                    : FiXCircle
                }
                color={
                  numPrepared >= MINIMUM_SOURCES_FOR_LINKAGE
                    ? 'success'
                    : 'error'
                }
              />
              <Typography>
                At least {MINIMUM_SOURCES_FOR_LINKAGE} data sources have been
                submitted ({numPrepared} / {job.progress.total} submitted).
                {!(canFinalize || canRerun) &&
                  numPrepared < MINIMUM_SOURCES_FOR_LINKAGE && (
                    <Link
                      to={`/user/workspace/${batchId}/overview`}
                      className={styles.checklistActionLink}
                    >
                      Contribute Data
                    </Link>
                  )}
              </Typography>
            </HStack>
          </li>
          <li key="check-status">
            <HStack spacing="sm" alignItems="center">
              <Icon
                as={canFinalize || canRerun ? FiCheckCircle : FiXCircle}
                color={canFinalize || canRerun ? 'success' : 'error'}
              />
              <Typography>
                Job is in a valid state for this action. Current:{' '}
                <Badge variant="default" size="sm">
                  {job.status.replace(/_/g, ' ')}
                </Badge>
              </Typography>
            </HStack>
          </li>
        </Stack>
      </Card>

      <Button
        variant="primary"
        size="lg"
        leftIcon={<Icon as={FiZap} />}
        onClick={() => setShowConfirmModal(true)}
        disabled={!(canFinalize || canRerun) || finalizeJobMutation.isPending}
        isLoading={finalizeJobMutation.isPending}
        title={
          !(canFinalize || canRerun)
            ? `Cannot run linkage until all checklist items are met.`
            : ''
        }
      >
        {actionText}
      </Button>

      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={modalTitle}
        size="md"
        footer={
          <HStack spacing="sm" justifyContent="flex-end">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={finalizeJobMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmAndFinalize}
              isLoading={finalizeJobMutation.isPending}
              disabled={
                finalizeJobMutation.isPending || confirmationText !== 'FINALIZE'
              }
            >
              Confirm and Run
            </Button>
          </HStack>
        }
      >
        <Stack spacing="md">
          <Alert variant="warning" icon={<Icon as={FiAlertTriangle} />}>
            {modalWarning}
          </Alert>
          <Stack
            spacing="xs"
            p="md"
            style={{
              backgroundColor: 'var(--cipher-color-background-page)',
              borderRadius: 'var(--cipher-border-radius-base)',
            }}
          >
            <Typography fontWeight="semibold">You are about to:</Typography>
            <Stack as="ul" pl="md" spacing="xs" style={{ listStyle: 'disc' }}>
              <li>
                <Typography variant="body2">
                  Finalize Job ID: <strong>{job.id}</strong>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Use data from{' '}
                  <strong>{numPrepared} submitted source(s)</strong>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Queue the job for processing
                </Typography>
              </li>
            </Stack>
          </Stack>
          <Input
            label={
              <span>
                Please type <strong>FINALIZE</strong> to confirm.
              </span>
            }
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            autoFocus
            disabled={finalizeJobMutation.isPending}
          />
        </Stack>
      </Modal>
    </Stack>
  );
};

export default RunLinkageTab;
